import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { kebabCase } from 'case-anything';
import colors from 'open-color';
import 'linaria';

/**
 * Get the remirror variable from the keys to access it in the them object.
 */
function getCustomPropertyName(keys) {
  return "--remirror-".concat(keys.map(kebabCase).join('-'));
}
/**
 * Get the custom property from the keys used to access it in the theme object.
 */


function getVar(keys) {
  return "var(".concat(getCustomPropertyName(keys), ")");
}

function keyCapturingProxy(getter) {
  var keys = [];

  function createProxy(obj) {
    /**
     * Track the number of times this has been called. This proxy only supports
     * simple getters which access the keys only without any extra functionality.
     */
    var called = false;
    return new Proxy(obj, {
      get: (_, key) => {
        if (called) {
          throw new Error("Must only access the key once. ".concat(key.toString()));
        }

        called = true;
        keys.push(key);
        return createProxy({});
      },
      set: (_, key) => {
        throw new Error("Setters are not allowed for this object. ".concat(key.toString())); // invariant(false, { message: `Setters are not allowed for this object. ${key.toString()}` });
      }
    });
  }

  getter(createProxy({}));
  return keys;
}
/**
 * Get the theme custom property.
 */


function getTheme(getter) {
  return getVar(keyCapturingProxy(getter));
}
/**
 * The default remirror theme. This can be mutated with the
 * `mutateRemirrorTheme`.
 */

var defaultRemirrorTheme = {
  color: {
    black: '#000000',
    white: '#ffffff',
    text: '#252103',
    background: '#ffffff',
    primary: '#7963d2',
    secondary: '#bcd263',
    muted: colors.gray[1],
    shadow1: 'rgba(10,31,68,0.08)',
    shadow2: 'rgba(10,31,68,0.10)',
    shadow3: 'rgba(10,31,68,0.12)'
  },
  hue: {
    gray: colors.gray,
    red: colors.red,
    pink: colors.pink,
    grape: colors.grape,
    violet: colors.violet,
    indigo: colors.indigo,
    blue: colors.blue,
    cyan: colors.cyan,
    teal: colors.teal,
    green: colors.green,
    lime: colors.lime,
    yellow: colors.yellow,
    orange: colors.orange
  },
  fontFamily: {
    default: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    heading: 'inherit',
    mono: 'Menlo, monospace'
  },
  fontSize: {
    0: '12px',
    1: '14px',
    2: '16px',
    3: '20px',
    4: '24px',
    5: '32px',
    6: '48px',
    7: '64px',
    8: '96px',
    default: '16px'
  },
  space: {
    1: '4px',
    2: '8px',
    3: '16px',
    4: '32px',
    5: '64px',
    6: '128px',
    7: '256px',
    8: '512px'
  },
  fontWeight: {
    bold: '700',
    default: '400',
    heading: '700'
  },
  letterSpacing: {
    tight: '-1px',
    default: 'normal',
    loose: '1px',
    wide: '3px'
  },
  lineHeight: {
    heading: '1.25em',
    default: '1.5em'
  }
};

/**
 * Create the theme variables from the provided theme.
 *
 * This function can't use anything from `@remirror/core-helpers` due to being
 * used in the `themeStyles` css. Babel can't resolve the imported functions in
 * development.
 */
function createThemeVariables() {
  var theme = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var cssVariableString = [];
  var cssVariableObject = {};

  function addCssVariable(keys, value) {
    if (typeof value === 'string' || typeof value === 'number') {
      cssVariableString.push("".concat(getCustomPropertyName(keys), ": ").concat(value, ";"));
      cssVariableObject[getCustomPropertyName(keys)] = value;
      return;
    }

    if (typeof value !== 'object' || !value) {
      return;
    }

    for (var _i = 0, _Object$entries = Object.entries(value); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          key = _Object$entries$_i[0],
          v = _Object$entries$_i[1];

      addCssVariable([...keys, key], v);
    }

    return;
  }

  for (var _i2 = 0, _Object$entries2 = Object.entries(theme); _i2 < _Object$entries2.length; _i2++) {
    var _Object$entries2$_i = _slicedToArray(_Object$entries2[_i2], 2),
        key = _Object$entries2$_i[0],
        value = _Object$entries2$_i[1];

    addCssVariable([key], value);
  }

  return {
    css: cssVariableString.join('\n'),
    styles: cssVariableObject
  };
}

/**
 * The class name for adding theme styles to the remirror editor.
 *
 * These are the variable names
 */

var themeStyles = "remirror-theme";

export { createThemeVariables, defaultRemirrorTheme, getTheme, themeStyles };
